// frontend/components/HrEmployeeCalendarTab.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchMyCalendar } from "../features/hr/hrThunks.js";
import { CalendarDays, Sun, Briefcase } from "lucide-react";
import "../styles/hr/hremployeecalendar.css";

const monthNames = [
  "Enero",
  "Febrero",
  "Marzo",
  "Abril",
  "Mayo",
  "Junio",
  "Julio",
  "Agosto",
  "Septiembre",
  "Octubre",
  "Noviembre",
  "Diciembre",
];

const typeLabels = {
  work: "Trabajo",
  vacation: "Vacaciones",
  day_off: "Día libre",
  sick_leave: "Baja médica",
  other: "Otros",
};

const typeColors = {
  work: "#22c55e",
  vacation: "#f97316",
  day_off: "#0ea5e9",
  sick_leave: "#ef4444",
  other: "#a855f7",
};

const getErrorText = (errorValue) => {
  if (!errorValue) return null;
  if (typeof errorValue === "string") return errorValue;
  return errorValue?.detail || "Ha ocurrido un error.";
};

// Evita el desfase típico de new Date("YYYY-MM-DD") (UTC)
const parseLocalDateFromYMD = (ymd) => {
  if (!ymd) return null;

  const parts = String(ymd).split("-");
  if (parts.length !== 3) return null;

  const year = Number(parts[0]);
  const month = Number(parts[1]);
  const day = Number(parts[2]);

  if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day)) {
    return null;
  }

  return new Date(year, month - 1, day);
};

const HrEmployeeCalendarTab = () => {
  const dispatch = useDispatch();
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

  const myCalendar = useSelector((state) => state.hr.myCalendar);
  const loadingCalendar = useSelector((state) => state.hr.loadingCalendar);
  const errorCalendar = useSelector((state) => state.hr.errorCalendar);

  useEffect(() => {
    dispatch(fetchMyCalendar({ year: selectedYear }));
  }, [dispatch, selectedYear]);

  const events = Array.isArray(myCalendar?.events) ? myCalendar.events : [];

  const eventsByMonth = useMemo(() => {
    const grouped = Array.from({ length: 12 }, () => []);

    events.forEach((eventItem) => {
      const date = parseLocalDateFromYMD(eventItem?.date);
      if (!date) return;

      const monthIndex = date.getMonth();
      if (monthIndex < 0 || monthIndex > 11) return;

      grouped[monthIndex].push(eventItem);
    });

    // ordenar por día (asc)
    grouped.forEach((list) => {
      list.sort((a, b) => {
        const da = String(a?.date || "");
        const db = String(b?.date || "");
        return da.localeCompare(db);
      });
    });

    return grouped;
  }, [events]);

  const stats = useMemo(() => {
    const base = {
      total: events.length,
      work: 0,
      vacation: 0,
      day_off: 0,
      sick_leave: 0,
      other: 0,
    };

    events.forEach((item) => {
      if (item?.type === "work") base.work += 1;
      else if (item?.type === "vacation") base.vacation += 1;
      else if (item?.type === "day_off") base.day_off += 1;
      else if (item?.type === "sick_leave") base.sick_leave += 1;
      else base.other += 1;
    });

    return base;
  }, [events]);

  const handleYearInputChange = (event) => {
    const value = event.target.value;

    if (value === "") return;

    const parsed = Number(value);
    if (Number.isFinite(parsed)) {
      setSelectedYear(parsed);
    }
  };

  const errorText = getErrorText(errorCalendar);

  return (
    <div className="hr-calendar-tab">
      {/* Header */}
      <div className="hr-calendar-header-card">
        <div className="hr-calendar-header-left">
          <p className="hr-calendar-header-year">Calendario {selectedYear}</p>
          <h2 className="hr-calendar-header-title">Calendario anual personal</h2>
          <p className="hr-calendar-header-subtitle">
            Visualiza tus días de trabajo, vacaciones y ausencias a lo largo del año.
          </p>
        </div>

        <div className="hr-calendar-header-right">
          <span className="hr-calendar-year-label">Año</span>
          <div className="hr-calendar-controls">
            <button
              type="button"
              className="hr-calendar-year-btn"
              onClick={() => setSelectedYear((prev) => prev - 1)}
              aria-label="Año anterior"
            >
              −
            </button>

            <input
              type="number"
              value={selectedYear}
              onChange={handleYearInputChange}
              min="2000"
              max="2100"
            />

            <button
              type="button"
              className="hr-calendar-year-btn"
              onClick={() => setSelectedYear((prev) => prev + 1)}
              aria-label="Año siguiente"
            >
              +
            </button>
          </div>
        </div>
      </div>

      {loadingCalendar ? (
        <div className="hr-calendar-loading">Cargando calendario...</div>
      ) : errorText ? (
        <div className="hr-error-text">Error: {errorText}</div>
      ) : !myCalendar ? (
        <div className="hr-calendar-empty-state">No hay datos de calendario.</div>
      ) : (
        <>
          {/* Métricas rápidas */}
          <div className="hr-calendar-metrics">
            <div className="hr-metric-card hr-metric-card--primary">
              <div className="hr-metric-top">
                <span className="hr-metric-label">Días marcados</span>
                <CalendarDays className="hr-metric-icon" aria-hidden="true" />
              </div>
              <div className="hr-metric-value">
                {stats.total}
                <span className="hr-metric-unit">días</span>
              </div>
              <p className="hr-metric-helper">
                Total de días con algún tipo de evento este año.
              </p>
            </div>

            <div className="hr-metric-card">
              <div className="hr-metric-top">
                <span className="hr-metric-label">Vacaciones</span>
                <Sun className="hr-metric-icon" aria-hidden="true" />
              </div>
              <div className="hr-metric-value">
                {stats.vacation}
                <span className="hr-metric-unit">días</span>
              </div>
              <p className="hr-metric-helper">
                Días marcados como vacaciones en el calendario.
              </p>
            </div>

            <div className="hr-metric-card">
              <div className="hr-metric-top">
                <span className="hr-metric-label">Trabajo / otros</span>
                <Briefcase className="hr-metric-icon" aria-hidden="true" />
              </div>
              <div className="hr-metric-value">
                {stats.work + stats.day_off + stats.sick_leave + stats.other}
                <span className="hr-metric-unit">días</span>
              </div>
              <p className="hr-metric-helper">
                Trabajo, días libres, bajas y otros eventos.
              </p>
            </div>
          </div>

          {/* Grid anual */}
          <div className="hr-calendar-year-grid">
            {monthNames.map((monthName, monthIndex) => {
              const monthEvents = eventsByMonth[monthIndex] || [];

              return (
                <div key={monthName} className="hr-calendar-month-card">
                  <div className="hr-calendar-month-header">
                    <span className="hr-calendar-month-name">{monthName}</span>
                    <span className="hr-calendar-month-count">
                      {monthEvents.length} días
                    </span>
                  </div>

                  {monthEvents.length === 0 ? (
                    <div className="hr-calendar-empty">Sin eventos</div>
                  ) : (
                    <ul className="hr-calendar-events-list">
                      {monthEvents.map((eventItem, index) => {
                        const date = parseLocalDateFromYMD(eventItem?.date);
                        const dayNumber = date ? date.getDate() : "—";

                        const typeKey = eventItem?.type || "other";
                        const typeLabel = typeLabels[typeKey] || typeKey;
                        const color = typeColors[typeKey] || "#64748b";

                        return (
                          <li
                            key={`${eventItem?.date || "date"}-${index}`}
                            className="hr-calendar-event-row"
                          >
                            <span
                              className="hr-calendar-dot"
                              style={{ backgroundColor: color }}
                            />
                            <span className="hr-calendar-day">{dayNumber}</span>
                            <span className="hr-calendar-type">{typeLabel}</span>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
              );
            })}
          </div>

          {/* Leyenda */}
          <div className="hr-calendar-legend-card">
            <span className="hr-calendar-legend-title">Leyenda</span>
            <div className="hr-calendar-legend-items">
              {Object.entries(typeLabels).map(([key, label]) => (
                <div key={key} className="hr-calendar-legend-item">
                  <span
                    className="hr-calendar-dot"
                    style={{ backgroundColor: typeColors[key] || "#64748b" }}
                  />
                  <span>{label}</span>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default HrEmployeeCalendarTab;